import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { X } from 'lucide-react';
import type { WindowProps } from './types';

const MIN_W = 400;
const MIN_H = 300;
type Corner = 'nw' | 'ne' | 'sw' | 'se';
const CORNER_CURSOR: Record<Corner, string> = {
  nw: 'nw-resize', ne: 'ne-resize', sw: 'sw-resize', se: 'se-resize',
};
// Each corner shows an L-shaped border (two adjacent edges) as the resize grip
const CORNER_BORDER: Record<Corner, React.CSSProperties> = {
  nw: { borderTopWidth: 2, borderLeftWidth: 2 },
  ne: { borderTopWidth: 2, borderRightWidth: 2 },
  sw: { borderBottomWidth: 2, borderLeftWidth: 2 },
  se: { borderBottomWidth: 2, borderRightWidth: 2 },
};

export function OSWindow({
  id, title, icon, children, onClose, isActive, onFocus,
  onMinimize, onMinimizeComplete, isMinimized, t,
  colorClass = 'from-celestial-mars to-celestial-saturn',
  width = 'auto', height = 'auto', zIndex = 10,
}: WindowProps) {
  const parseSize = (v: string | number) => parseInt(String(v)) || 1050;

  const [isMaximized, setIsMaximized] = useState(false);
  const [snapZone, setSnapZone] = useState<'none' | 'left' | 'right'>('none');
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [winSize, setWinSize] = useState({ w: parseSize(width), h: parseSize(height) });
  const [pos, setPos] = useState(() => {
    const w = parseSize(width);
    const h = parseSize(height);
    const topReserved = 56;       // 顶部状态栏高度
    const bottomReserved = 104;   // 底部 Dock 高度
    const maxX = Math.max(0, window.innerWidth - w);
    const maxY = Math.max(0, window.innerHeight - bottomReserved - h);
    const x = Math.max(0, Math.min(maxX, (window.innerWidth - w) / 2));
    const y = Math.max(topReserved, Math.min(maxY, (window.innerHeight - h) / 2));
    return { x, y };
  });

  const windowRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);
  const resizeRef = useRef<{
    corner: Corner; mx: number; my: number;
    sx: number; sy: number; sw: number; sh: number;
  } | null>(null);
  // v3.12.11 P1-6: 存储当前活跃的拖拽/缩放清理函数，组件卸载或失焦时清理，防监听器泄漏
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const posRef = useRef(pos);
  const sizeRef = useRef(winSize);
  posRef.current = pos;
  sizeRef.current = winSize;

  useEffect(() => {
    setWinSize({ w: parseSize(width), h: parseSize(height) });
  }, [width, height]);

  useEffect(() => {
    // v3.12.11 P1-6: 失焦时清理拖拽/缩放监听器，防止泄漏
    const handleBlur = () => {
      dragCleanupRef.current?.();
      dragCleanupRef.current = null;
      resizeCleanupRef.current?.();
      resizeCleanupRef.current = null;
      setIsDragging(false);
      setIsResizing(false);
    };
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('blur', handleBlur);
      handleBlur(); // 组件卸载时也清理
      dragRef.current = null;
      resizeRef.current = null;
    };
  }, []);

  const isLocked = isMaximized || isMinimized;
  const isSnapped = isMaximized || snapZone !== 'none';

  // ── Drag ──
  const onHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    if (isLocked) return;
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    setIsDragging(true);
    onFocus(id);
    dragRef.current = { mx: e.clientX, my: e.clientY, px: posRef.current.x, py: posRef.current.y };
    const w = sizeRef.current.w;
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setPos({
        x: Math.max(-w + 80, Math.min(window.innerWidth - 80, dragRef.current.px + ev.clientX - dragRef.current.mx)),
        y: Math.max(0, Math.min(window.innerHeight - 40, dragRef.current.py + ev.clientY - dragRef.current.my)),
      });
    };
    const onUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      dragCleanupRef.current = null;
      setIsDragging(false);
      if (ev.clientX < 80) setSnapZone('left');
      else if (ev.clientX > window.innerWidth - 80) setSnapZone('right');
      else setSnapZone('none');
      dragRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    // v3.12.11 P1-6: 存储清理函数，失焦或卸载时调用
    dragCleanupRef.current = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isLocked, onFocus, id]);

  // ── Resize ──
  const onResizeMouseDown = useCallback((corner: Corner) => (e: React.MouseEvent) => {
    if (isLocked) return;
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);

    const rect = windowRef.current?.getBoundingClientRect();
    let sx = posRef.current.x, sy = posRef.current.y, sw = sizeRef.current.w, sh = sizeRef.current.h;
    if (rect) { sx = rect.left; sy = rect.top; sw = rect.width; sh = rect.height; }
    if (snapZone !== 'none') {
      setSnapZone('none');
      setPos({ x: sx, y: sy });
      setWinSize({ w: sw, h: sh });
    }

    resizeRef.current = { corner, mx: e.clientX, my: e.clientY, sx, sy, sw, sh };

    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const r = resizeRef.current;
      let nw = r.sw, nh = r.sh, nx = r.sx, ny = r.sy;
      const dx = ev.clientX - r.mx, dy = ev.clientY - r.my;

      if (r.corner.includes('e')) nw = Math.max(MIN_W, r.sw + dx);
      else if (r.corner.includes('w')) { nw = Math.max(MIN_W, r.sw - dx); nx = r.sx + (r.sw - nw); }
      if (r.corner.includes('s')) nh = Math.max(MIN_H, r.sh + dy);
      else if (r.corner.includes('n')) { nh = Math.max(MIN_H, r.sh - dy); ny = r.sy + (r.sh - nh); }

      setWinSize({ w: nw, h: nh });
      setPos({ x: nx, y: ny });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      resizeCleanupRef.current = null;
      setIsResizing(false);
      resizeRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    // v3.12.11 P1-6: 存储清理函数
    resizeCleanupRef.current = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isLocked, snapZone]);

  // ── Handlers ──
  const handleAnimComplete = useCallback(() => {
    if (isMinimized) onMinimizeComplete(id);
  }, [isMinimized, onMinimizeComplete, id]);
  const handleFocus = useCallback(() => {
    if (!isMinimized) onFocus(id);
  }, [isMinimized, onFocus, id]);
  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onClose(id);
  }, [onClose, id]);

  // ── Style ──
  const winStyles: React.CSSProperties = {
    zIndex: isMinimized ? zIndex - 100 : zIndex,
    position: isSnapped ? 'fixed' : 'absolute',
    width: isMaximized ? '100vw' : snapZone !== 'none' ? '50vw' : `${winSize.w}px`,
    height: isMaximized ? 'calc(100vh - 40px)' : snapZone !== 'none' ? 'calc(100vh - 40px)' : `${winSize.h}px`,
  };
  if (isSnapped) {
    winStyles.top = '40px';
    winStyles.left = isMaximized ? '0' : snapZone === 'left' ? '0' : snapZone === 'right' ? '50%' : undefined;
  } else {
    winStyles.left = `${pos.x}px`;
    winStyles.top = `${pos.y}px`;
  }

  return (
    <motion.div
      ref={windowRef}
      initial={{ opacity: 0, scale: 0.85, y: 20, filter: 'blur(0px)' }}
      animate={isMinimized
        ? { opacity: 0, scale: 0.3, y: 40, filter: 'blur(4px)', transition: { duration: 0.25, ease: [0.4, 0, 1, 1] } }
        : { opacity: 1, scale: 1, y: 0, filter: 'blur(0px)' }
      }
      onAnimationComplete={handleAnimComplete}
      exit={{ opacity: 0, scale: 0.85, y: 20, filter: 'blur(4px)', transition: { duration: 0.18, ease: [0.4, 0, 1, 1] } }}
      style={winStyles}
      onClick={handleFocus}
      className={`os-window pointer-events-auto ${isMaximized ? 'rounded-none' : 'rounded-3xl'} ${isMinimized ? 'pointer-events-none' : ''} ${isDragging ? 'is-dragging' : ''}`}
    >
      <div className="os-window-header" onMouseDown={onHeaderMouseDown}>
        <div className="flex min-w-0 items-center gap-3 select-none">
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${colorClass} p-1.5 shadow-lg ring-1 ring-white/15`}>
            {React.isValidElement(icon)
              ? React.cloneElement(icon as React.ReactElement<any>, { size: 16, className: 'text-white' })
              : icon}
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-xs font-black uppercase leading-none tracking-[0.16em] text-white/80">{title}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-white/45 transition-colors hover:border-red-400/30 hover:bg-red-500/15 hover:text-red-100"
            title={t.close || '关闭'}
          >
            <X size={15} />
          </button>
        </div>
      </div>

      <div
        className="os-window-content bg-[#05050a]/98 backdrop-blur-3xl h-full"
        style={(isDragging || isResizing) ? { backdropFilter: 'none' } : undefined}
      >
        {children}
      </div>

      {/* Four corner resize handles — kept INSIDE the window edge so the bottom
          corners are not clipped by the desktop dock; hidden when locked/snapped */}
      {!isLocked && snapZone === 'none' && (
        <>{(['nw', 'ne', 'sw', 'se'] as Corner[]).map(corner => (
          <div
            key={corner}
            onMouseDown={onResizeMouseDown(corner)}
            className="resize-handle absolute z-20"
            style={{
              width: 22, height: 22,
              cursor: CORNER_CURSOR[corner],
              ...(corner.includes('n') ? { top: 0 } : { bottom: 0 }),
              ...(corner.includes('w') ? { left: 0 } : { right: 0 }),
              ...CORNER_BORDER[corner],
            }}
          />
        ))}</>
      )}
    </motion.div>
  );
}
